/**
 * Viewpoint preset module.
 * Defines the supported preset names plus the seed directions and baseline framing
 * distances used as the planner's starting point.
 */
import type { ViewpointPreset, ViewpointVector3 } from '../generate-jig.types'

/**
 * Static seed values for each supported viewpoint preset.
 */
export interface ViewpointPresetConfig {
  position: ViewpointVector3
  targetY: number
  distance: number
}

/**
 * Canonical camera directions and baseline framing values for the supported presets.
 */
export const VIEWPOINT_PRESETS: Record<ViewpointPreset, ViewpointPresetConfig> = {
  front: { position: { x: 0, y: 0.3, z: 1.5 }, targetY: 0, distance: 1.5 },
  back: { position: { x: 0, y: 0.3, z: -1.5 }, targetY: 0, distance: 1.5 },
  top: { position: { x: 0, y: 2.0, z: 0.3 }, targetY: 0, distance: 2.0 },
  side: { position: { x: 1.5, y: 0.3, z: 0 }, targetY: 0, distance: 1.5 },
  left: { position: { x: -1.5, y: 0.3, z: 0 }, targetY: 0, distance: 1.5 },
  'close-up': { position: { x: 0, y: 0.2, z: 0.8 }, targetY: 0, distance: 0.8 },
  wide: { position: { x: 0, y: 0.5, z: 3.0 }, targetY: 0, distance: 3.0 },
  'low-angle': { position: { x: 0, y: -0.2, z: 1.2 }, targetY: 0, distance: 1.3 },
  'high-angle': { position: { x: 0.5, y: 1.5, z: 1.0 }, targetY: 0, distance: 1.8 },
  'rear-high-angle': { position: { x: -0.5, y: 1.5, z: -1.0 }, targetY: 0, distance: 1.8 },
}

/**
 * How much the planner should expand target size into viewing distance for each preset.
 */
export const VIEWPOINT_DISTANCE_MULTIPLIERS: Record<ViewpointPreset, number> = {
  front: 1.5,
  back: 1.5,
  top: 2.0,
  side: 1.5,
  left: 1.5,
  'close-up': 0.8,
  wide: 2.5,
  'low-angle': 1.3,
  'high-angle': 1.8,
  'rear-high-angle': 1.8,
}

/**
 * Runtime guard for AI-provided preset names.
 */
export function isViewpointPreset(value: any): value is ViewpointPreset {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(VIEWPOINT_PRESETS, value)
}
